import { z } from 'zod';
// define tool names
export const ToolNameSchema = z.enum([
    'generateDashboard',
    'generateHashes',
    'generateUuids',
    'findContent',
    'listContent',
    'findExplores',
    'findCustomChartTypes',
    'findFields',
    'searchSemanticLayer',
    'analyzeFieldImpact',
    'discoverFields',
    'grepFields',
    'getMetadata',
    'searchFieldValues',
    'findDashboards',
    'findCharts',
    'getDashboardCharts',
    'readContent',
    'resolveUrl',
    'editContent',
    'createContent',
    'createScheduledDelivery',
    'updateUserName',
    'runContentQuery',
    'listProjects',
    'getProjectInfo',
    'loadSkill',
    'loadProjectContext',
    'loadMcpTools',
    'generateDataApp',
    'iterateDataApp',
    'listDataAppThemes',
    'editDbtProject',
    'editProjectContext',
    'editRepo',
    'syncDbtProject',
    'exploreRepo',
    'discoverRepos',
    'listWorkstreams',
    'closePullRequest',
    'getPullRequestDiff',
    'generateVisualization',
    'setupPreviewDeploy',
    'runQuery',
    'runSavedChart',
    'runSql',
    'runComposerQueries',
    'listWarehouseTables',
    'describeWarehouseTable',
    'listKnowledgeDocuments',
    'getKnowledgeDocumentContent',
    'readPinnedThread',
    'readAttachments',
    'submitResearchReport',
    'delegateResearchTask',
    'submitWorkerFindings',
]);
export const isToolName = (toolName) => ToolNameSchema.safeParse(toolName).success;
// display messages schema
export const ToolDisplayMessagesSchema = z.partialRecord(ToolNameSchema, z.string());
export const TOOL_DISPLAY_MESSAGES = ToolDisplayMessagesSchema.parse({
    findExplores: 'Finding relevant explores',
    findCustomChartTypes: 'Browsing custom chart types',
    findDashboards: 'Finding relevant dashboards',
    findContent: 'Finding relevant content',
    listContent: 'Listing content',
    findFields: 'Finding relevant fields',
    searchSemanticLayer: 'Searching the semantic layer',
    analyzeFieldImpact: 'Analyzing field impact',
    discoverFields: 'Discovering fields',
    grepFields: 'Searching fields',
    getMetadata: 'Reading metadata',
    searchFieldValues: 'Searching field values',
    generateDashboard: 'Generating a dashboard',
    generateHashes: 'Generating hashes',
    generateUuids: 'Generating UUIDs',
    findCharts: 'Finding relevant charts',
    getDashboardCharts: 'Looking up dashboard charts',
    readContent: 'Reading content',
    resolveUrl: 'Resolving link',
    editContent: 'Editing content',
    createContent: 'Creating content',
    createScheduledDelivery: 'Creating scheduled delivery',
    updateUserName: 'Saving your name',
    runContentQuery: 'Running content query',
    listProjects: 'Listing projects',
    getProjectInfo: 'Getting project details',
    loadSkill: 'Loading skill',
    loadProjectContext: 'Loading project context',
    loadMcpTools: 'Loading MCP tools',
    generateDataApp: 'Starting a data app build',
    iterateDataApp: 'Iterating on a data app',
    editDbtProject: 'Editing semantic layer',
    editRepo: 'Editing repository',
    syncDbtProject: 'Syncing dbt project',
    exploreRepo: 'Reading repository',
    discoverRepos: 'Discovering repositories',
    listWorkstreams: 'Listing pull requests',
    closePullRequest: 'Closing pull request',
    getPullRequestDiff: 'Reading pull request diff',
    generateVisualization: 'Generating visualization',
    setupPreviewDeploy: 'Setting up preview deploys',
    runQuery: 'Running query',
    runSavedChart: 'Running saved chart',
    runSql: 'Running SQL query',
    runComposerQueries: 'Running composer queries',
    listWarehouseTables: 'Listing warehouse tables',
    describeWarehouseTable: 'Describing warehouse table',
    listKnowledgeDocuments: 'Listing knowledge documents',
    getKnowledgeDocumentContent: 'Reading knowledge document',
    readPinnedThread: 'Reading pinned conversation',
    readAttachments: 'Reading attached documents',
    submitResearchReport: 'Saving research report',
    delegateResearchTask: 'Delegating a research task',
    submitWorkerFindings: 'Saving task findings',
});
// after-tool-call messages
export const TOOL_DISPLAY_MESSAGES_AFTER_TOOL_CALL = ToolDisplayMessagesSchema.parse({
    findExplores: 'Found relevant explores',
    findCustomChartTypes: 'Browsed custom chart types',
    findDashboards: 'Found relevant dashboards',
    findFields: 'Found relevant fields',
    searchSemanticLayer: 'Searched the semantic layer',
    analyzeFieldImpact: 'Analyzed field impact',
    discoverFields: 'Discovered fields',
    findContent: 'Found relevant content',
    listContent: 'Listed content',
    searchFieldValues: 'Found field values',
    generateDashboard: 'Generated a dashboard',
    generateHashes: 'Generated hashes',
    generateUuids: 'Generated UUIDs',
    grepFields: 'Searched fields',
    getMetadata: 'Read metadata',
    findCharts: 'Found relevant charts',
    getDashboardCharts: 'Found dashboard charts',
    readContent: 'Read content',
    resolveUrl: 'Resolved link',
    editContent: 'Edited content',
    createContent: 'Created content',
    createScheduledDelivery: 'Created scheduled delivery',
    updateUserName: 'Saved your name',
    runContentQuery: 'Ran content query',
    listProjects: 'Listed projects',
    getProjectInfo: 'Got project details',
    loadSkill: 'Loaded skill',
    loadProjectContext: 'Loaded project context',
    loadMcpTools: 'Loaded MCP tools',
    generateDataApp: 'Started a data app build',
    iterateDataApp: 'Started a data app build',
    editDbtProject: 'Edited semantic layer',
    editRepo: 'Edited repository',
    syncDbtProject: 'Synced dbt project',
    exploreRepo: 'Read repository',
    discoverRepos: 'Discovered repositories',
    listWorkstreams: 'Listed pull requests',
    closePullRequest: 'Closed pull request',
    getPullRequestDiff: 'Read pull request diff',
    generateVisualization: 'Generated visualization',
    setupPreviewDeploy: 'Opened preview-deploy pull request',
    runQuery: 'Ran query',
    runSavedChart: 'Ran saved chart',
    runSql: 'Ran SQL query',
    runComposerQueries: 'Ran composer queries',
    listWarehouseTables: 'Listed warehouse tables',
    describeWarehouseTable: 'Described warehouse table',
    listKnowledgeDocuments: 'Listed knowledge documents',
    getKnowledgeDocumentContent: 'Read knowledge document',
    readPinnedThread: 'Read pinned conversation',
    readAttachments: 'Read attached documents',
    submitResearchReport: 'Saved research report',
    delegateResearchTask: 'Delegated a research task',
    submitWorkerFindings: 'Saved task findings',
});
//# sourceMappingURL=index.js.map